import { useQuery } from '@tanstack/react-query';
import {
  Activity, BookOpen, CheckCircle, CreditCard, Shield, TrendingUp,
  Zap, Eye, AlertTriangle, ArrowRight, RefreshCw, Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MetricCard, PageHeader, SectionEmptyState } from '@/components/ui';
import { GlowAreaChart, type AreaSeries } from '@/components/ui/charts/GlowAreaChart';
import { DonutChart } from '@/components/ui/charts/DonutChart';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import type { UsageAnalytics } from '@/features/analytics/api';
import { fetchMyClient } from '@/features/clients/api';
import { ActivationWorkspaceScreen } from './ActivationWorkspaceScreen';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OverviewKpis {
  todayAuthChecks: number;
  todayAuthChecksDelta: number;
  authSuccessRate: number;
  authSuccessRateDelta: number;
  visitorsIdentified: number;
  visitorsIdentifiedDelta: number;
  remainingCredits: number;
  balanceExpiryDate: string | null;
}

interface RiskSummary {
  totalEvents: number;
  allowedCount: number;
  challengeCount: number;
  reviewCount: number;
  blockedCount: number;
  lowRiskCount: number;
  mediumRiskCount: number;
  highRiskCount: number;
  criticalRiskCount: number;
  averageTrustScore: number;
  averageRiskScore: number;
}

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchOverview(): Promise<OverviewKpis> {
  const res = await api.get<ResponseEnvelope<OverviewKpis>>(Endpoints.me.overview);
  return unwrap(res);
}
async function fetchClientUsage(): Promise<UsageAnalytics> {
  const to = new Date().toISOString();
  const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const res = await api.get<ResponseEnvelope<UsageAnalytics>>(Endpoints.me.usage, {
    params: { from, to, granularity: 'DAILY' },
  });
  return unwrap(res);
}
async function fetchRiskSummary(): Promise<RiskSummary> {
  const res = await api.get<ResponseEnvelope<RiskSummary>>(Endpoints.me.riskSummary);
  return unwrap(res);
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DARK_GLASS = {
  background: 'linear-gradient(135deg, #050e1d 0%, #060f22 100%)',
  borderColor: 'rgba(96,165,250,0.1)',
} as const;

const DARK_DIVIDER = { borderColor: 'rgba(255,255,255,0.06)' } as const;

const SERVICE_LABELS: Record<string, string> = {
  VERIFY_N: 'Verify N', VERIFY_N_PLUS: 'Verify N+',
  OPPORTUNITY: 'Signal', SIGNAL: 'Signal',
  SHIELD: 'Shield', DETECT: 'Detect', TRUST: 'Trust', UNKNOWN: 'Unknown',
};

const SERVICE_COLORS: Record<string, string> = {
  VERIFY_N:      '#60a5fa',
  VERIFY_N_PLUS: '#a78bfa',
  SIGNAL:        '#34d399',
  OPPORTUNITY:   '#34d399',
  SHIELD:        '#10b981',
  DETECT:        '#fb923c',
  TRUST:         '#22d3ee',
  UNKNOWN:       '#64748b',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function LegendChip({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 6px ${color}80` }}
      />
      <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</span>
      {value > 0 && (
        <span className="text-[11px] font-semibold tabular-nums" style={{ color }}>
          {value.toLocaleString()}
        </span>
      )}
    </div>
  );
}

function ChartFooterStat({
  label,
  value,
  color,
  border,
}: {
  label: string;
  value: number;
  color?: string;
  border?: boolean;
}) {
  return (
    <div
      className={`px-5 py-3 ${border ? 'border-l' : ''}`}
      style={border ? DARK_DIVIDER : undefined}
    >
      <p
        className="text-[10px] font-semibold uppercase tracking-widest"
        style={{ color: 'rgba(255,255,255,0.3)' }}
      >
        {label}
      </p>
      <p
        className="mt-1 text-lg font-bold tabular-nums"
        style={{ color: color ?? 'white' }}
      >
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function RiskLevelCard({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-surface p-4 dark:bg-[#060f22]/80"
      style={{ borderColor: `${color}25` }}
    >
      <div
        className="absolute left-0 top-0 h-full w-0.5 rounded-full"
        style={{ background: color, boxShadow: `0 0 14px ${color}90` }}
      />
      <div className="pl-3">
        <p className="text-xs text-fg-muted">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-fg">{count.toLocaleString()}</p>
      </div>
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
  description,
  color,
}: {
  to: string;
  icon: typeof Shield;
  label: string;
  description: string;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-card transition-all duration-200 hover:-translate-y-[1px] hover:border-border-strong hover:shadow-card-md dark:shadow-card-dark"
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-200 group-hover:scale-110 ${color}`}
      >
        <Icon className="h-4.5 w-4.5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">{label}</p>
        <p className="text-xs text-fg-muted">{description}</p>
      </div>
      <ArrowRight
        className="h-4 w-4 shrink-0 text-fg-subtle transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </Link>
  );
}

function ErrorCallout({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-500/20 dark:bg-rose-500/10">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
      <p className="text-sm text-rose-700 dark:text-rose-300">{message}</p>
    </div>
  );
}

// ── Recommended actions ───────────────────────────────────────────────────────

interface RecommendedActionsProps {
  kpis: OverviewKpis | undefined;
  risk: RiskSummary | undefined;
  hasNoTraffic: boolean;
  balanceValid: boolean;
  daysUntilExpiry: number | null;
}

interface ActionItem {
  icon: typeof Shield;
  label: string;
  description: string;
  to: string;
  linkLabel: string;
  severity: 'critical' | 'warning' | 'info';
}

function RecommendedActions({
  kpis,
  risk,
  hasNoTraffic,
  balanceValid,
  daysUntilExpiry,
}: RecommendedActionsProps) {
  const actions: ActionItem[] = [];

  if (kpis && !balanceValid) {
    actions.push({
      icon: CreditCard, label: 'Balance expired',
      description: 'Your API credits have expired. All verification requests are being rejected.',
      to: '/portal/support', linkLabel: 'Contact support', severity: 'critical',
    });
  }
  if (kpis && balanceValid && kpis.remainingCredits > 0 && kpis.remainingCredits < 1000) {
    actions.push({
      icon: CreditCard, label: 'Low balance',
      description: `Only ${kpis.remainingCredits.toLocaleString()} credits remaining. Request a top-up before you run out.`,
      to: '/portal/requests', linkLabel: 'Request top-up', severity: 'warning',
    });
  }
  if (kpis && balanceValid && daysUntilExpiry !== null && daysUntilExpiry <= 14) {
    actions.push({
      icon: AlertTriangle, label: 'Package expiring soon',
      description: `Your balance expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}.`,
      to: '/portal/support', linkLabel: 'Request renewal', severity: 'warning',
    });
  }
  if (risk && (risk.highRiskCount + risk.criticalRiskCount) > 0) {
    actions.push({
      icon: Shield, label: 'High-risk events detected',
      description: `${risk.highRiskCount + risk.criticalRiskCount} high or critical risk events need attention.`,
      to: '/portal/risk-events', linkLabel: 'Review events', severity: 'warning',
    });
  }
  if (hasNoTraffic && kpis) {
    actions.push({
      icon: Zap, label: 'No recent API activity',
      description: "Your integration hasn't sent any requests in the last 7 days.",
      to: '/portal/docs', linkLabel: 'View integration guide', severity: 'info',
    });
  }

  if (actions.length === 0) return null;

  const severityStyles = {
    critical: 'border-rose-200 bg-rose-50 dark:border-rose-500/25 dark:bg-rose-500/[0.07]',
    warning:  'border-amber-200 bg-amber-50 dark:border-amber-500/25 dark:bg-amber-500/[0.07]',
    info:     'border-primary-200 bg-primary-50 dark:border-primary-500/25 dark:bg-primary-500/[0.07]',
  };
  const iconStyles = { critical: 'text-rose-500', warning: 'text-amber-500', info: 'text-primary-500' };
  const linkStyles = {
    critical: 'text-rose-600 dark:text-rose-400',
    warning:  'text-amber-700 dark:text-amber-400',
    info:     'text-primary-600 dark:text-primary-400',
  };

  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-fg">Recommended Actions</h2>
      <div className="space-y-2.5">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <div
              key={action.label}
              className={`flex items-start gap-3 rounded-2xl border px-5 py-4 ${severityStyles[action.severity]}`}
            >
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconStyles[action.severity]}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-fg">{action.label}</p>
                <p className="mt-0.5 text-xs text-fg-muted">{action.description}</p>
              </div>
              <Link
                to={action.to}
                className={`flex shrink-0 items-center gap-1 text-xs font-semibold transition-opacity hover:opacity-75 ${linkStyles[action.severity]}`}
              >
                {action.linkLabel}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ClientOverviewPage() {
  // Resolve activation status — ONBOARDING clients see the activation screen.
  // Uses ['me','myClient'] which is shared with ClientPortalGuard (cache hit).
  const clientStatusQuery = useQuery({
    queryKey: ['me', 'myClient'],
    queryFn: fetchMyClient,
    staleTime: 30_000,
  });

  const overviewQuery = useQuery({
    queryKey: ['me', 'overview'],
    queryFn: fetchOverview,
    refetchInterval: 60_000,
  });
  const usageQuery = useQuery({
    queryKey: ['me', 'usage', '7d'],
    queryFn: fetchClientUsage,
    staleTime: 5 * 60_000,
  });
  const riskQuery = useQuery({
    queryKey: ['me', 'risk-summary'],
    queryFn: fetchRiskSummary,
    staleTime: 5 * 60_000,
  });

  // All hooks called above — conditional return is safe here.
  if (clientStatusQuery.data?.clientStatus === 'ONBOARDING') {
    return <ActivationWorkspaceScreen />;
  }

  const kpis = overviewQuery.data;
  const usage = usageQuery.data;
  const risk = riskQuery.data;

  const balanceExpiry = kpis?.balanceExpiryDate ? new Date(kpis.balanceExpiryDate) : null;
  const balanceValid = balanceExpiry ? balanceExpiry > new Date() : false;
  const daysUntilExpiry = balanceExpiry
    ? Math.ceil((balanceExpiry.getTime() - Date.now()) / 86400_000)
    : null;

  const successRatePct = kpis ? Math.round(kpis.authSuccessRate * 100) : 0;

  // Chart series
  const activitySeries: AreaSeries[] = [
    {
      key: 'match',
      label: 'Match',
      values: (usage?.series ?? []).map((b) => b.success),
      lineColor: '#60a5fa',
      areaColor: '#3b82f6',
    },
    {
      key: 'nomatch',
      label: 'No match',
      values: (usage?.series ?? []).map((b) => b.failed),
      lineColor: '#fb7185',
      areaColor: '#f43f5e',
    },
  ];
  const activityLabels = (usage?.series ?? []).map((b) => b.bucket);
  const hasActivity = (usage?.series ?? []).some((b) => b.total > 0);

  // Mini stats
  const totalMatch = activitySeries[0]?.values.reduce((s, v) => s + v, 0) ?? 0;
  const totalNoMatch = activitySeries[1]?.values.reduce((s, v) => s + v, 0) ?? 0;
  const total7d = totalMatch + totalNoMatch;

  // Credit runway
  const dailyAvg =
    (usage?.series ?? []).length > 0
      ? (usage?.totalRequests ?? 0) / (usage?.series ?? []).length
      : 0;
  const runwayDays =
    dailyAvg > 0 && kpis ? Math.floor(kpis.remainingCredits / dailyAvg) : null;

  // Service donut segments
  const serviceSegments = usage
    ? Object.entries(usage.byServiceType)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([key, val]) => ({
          label: SERVICE_LABELS[key] ?? key,
          value: val,
          color: SERVICE_COLORS[key] ?? '#64748b',
        }))
    : [];
  const serviceTotal = serviceSegments.reduce((s, seg) => s + seg.value, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="My Workspace"
        subtitle="Live overview of your Authmatech integration activity."
        badge={
          overviewQuery.isFetching ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin text-fg-subtle" />
          ) : null
        }
      />

      {/* Error state */}
      {overviewQuery.isError && (
        <ErrorCallout
          message={
            isApiError(overviewQuery.error)
              ? overviewQuery.error.firstErrorMessage
              : 'Could not load overview metrics.'
          }
        />
      )}

      {/* Balance alert */}
      {kpis && (!balanceValid || (daysUntilExpiry !== null && daysUntilExpiry <= 14)) && (
        <div
          className={`flex items-start gap-3 rounded-2xl border px-5 py-4 ${
            !balanceValid
              ? 'border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10'
              : 'border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10'
          }`}
        >
          <AlertTriangle
            className={`mt-0.5 h-4 w-4 shrink-0 ${!balanceValid ? 'text-rose-500' : 'text-amber-500'}`}
          />
          <div>
            <p
              className={`text-sm font-semibold ${
                !balanceValid
                  ? 'text-rose-800 dark:text-rose-200'
                  : 'text-amber-800 dark:text-amber-200'
              }`}
            >
              {!balanceValid
                ? 'Balance expired — API calls will be rejected'
                : `Balance expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`}
            </p>
            <p
              className={`mt-0.5 text-xs ${
                !balanceValid
                  ? 'text-rose-600/80 dark:text-rose-400'
                  : 'text-amber-600/80 dark:text-amber-400'
              }`}
            >
              Contact your account manager to renew your plan.
            </p>
          </div>
        </div>
      )}

      {/* Hero balance card */}
      {kpis && (
        <div
          className="relative overflow-hidden rounded-2xl p-6 text-white"
          style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0f2040 40%, #0d3a8e 100%)' }}
        >
          <div
            className="pointer-events-none absolute -right-12 -top-12 h-64 w-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.12) 0%, transparent 70%)' }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-16 left-1/3 h-48 w-48 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.08) 0%, transparent 70%)' }}
            aria-hidden
          />

          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/50">
                Available Credits
              </p>
              <p className="mt-2 text-5xl font-extrabold tracking-tight">
                {kpis.remainingCredits.toLocaleString()}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {balanceExpiry && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">
                    <CreditCard className="h-3 w-3" />
                    {balanceValid
                      ? `Expires ${balanceExpiry.toLocaleDateString()}`
                      : 'Expired'}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">
                  <Activity className="h-3 w-3" />
                  {kpis.todayAuthChecks.toLocaleString()} today
                </span>
                {runwayDays !== null && (
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                      runwayDays < 7
                        ? 'bg-rose-500/30 text-white'
                        : runwayDays < 30
                        ? 'bg-amber-500/25 text-amber-200'
                        : 'bg-white/10 text-white/70'
                    }`}
                  >
                    ~{runwayDays}d runway
                  </span>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-5 py-3 text-center backdrop-blur-sm">
              <p className="text-3xl font-bold">{successRatePct}%</p>
              <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/60">
                Success rate
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Auth checks today"
          value={kpis?.todayAuthChecks ?? 0}
          delta={kpis ? kpis.todayAuthChecksDelta * 100 : undefined}
          icon={Activity}
          tone="default"
          loading={overviewQuery.isLoading}
          sparkline={activitySeries[0]?.values.slice(-7)}
          className="stagger-1"
        />
        <MetricCard
          label="Success rate"
          value={kpis ? `${(kpis.authSuccessRate * 100).toFixed(1)}%` : '—'}
          delta={kpis ? kpis.authSuccessRateDelta * 100 : undefined}
          icon={CheckCircle}
          tone="success"
          loading={overviewQuery.isLoading}
          className="stagger-2"
        />
        <MetricCard
          label="Visitors identified"
          value={kpis?.visitorsIdentified ?? 0}
          delta={kpis ? kpis.visitorsIdentifiedDelta * 100 : undefined}
          icon={Eye}
          tone="signal"
          loading={overviewQuery.isLoading}
          className="stagger-3"
        />
        <MetricCard
          label="Risk events"
          value={risk?.totalEvents ?? 0}
          subtitle={risk ? `${risk.blockedCount} blocked` : undefined}
          icon={Shield}
          className="stagger-4"
          tone={risk && risk.blockedCount > 0 ? 'danger' : 'shield'}
          loading={riskQuery.isLoading}
        />
      </div>

      {/* Charts row — dark glass containers */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* 7-day activity */}
        <div
          className="lg:col-span-2 overflow-hidden rounded-2xl border"
          style={DARK_GLASS}
        >
          {/* Header */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4"
            style={DARK_DIVIDER}
          >
            <div>
              <h2 className="text-sm font-semibold text-white">7-day activity</h2>
              <p className="mt-0.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                API verification requests
              </p>
            </div>
            <div className="flex items-center gap-4">
              <LegendChip color="#60a5fa" label="Match" value={totalMatch} />
              <LegendChip color="#fb7185" label="No match" value={totalNoMatch} />
            </div>
          </div>

          {/* Chart */}
          <div className="px-2 py-3">
            {usageQuery.isLoading ? (
              <div
                className="w-full animate-pulse rounded-lg"
                style={{
                  height: 185,
                  background: 'linear-gradient(180deg, rgba(96,165,250,0.08) 0%, transparent 100%)',
                }}
              />
            ) : hasActivity ? (
              <GlowAreaChart
                series={activitySeries}
                labels={activityLabels}
                height={185}
              />
            ) : (
              <SectionEmptyState
                icon={TrendingUp}
                title="No activity yet"
                description="Verification requests will appear here once you start making API calls."
                className="py-14"
              />
            )}
          </div>

          {/* Mini stat footer */}
          {hasActivity && (
            <div className="grid grid-cols-3 border-t" style={DARK_DIVIDER}>
              <ChartFooterStat label="Match" value={totalMatch} color="#60a5fa" />
              <ChartFooterStat label="No match" value={totalNoMatch} color="#fb7185" border />
              <ChartFooterStat label="7d total" value={total7d} border />
            </div>
          )}
        </div>

        {/* Service breakdown */}
        <div className="overflow-hidden rounded-2xl border" style={DARK_GLASS}>
          <div className="border-b px-5 py-4" style={DARK_DIVIDER}>
            <h2 className="text-sm font-semibold text-white">By service type</h2>
            {usage && (
              <p className="mt-0.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {serviceSegments.length} active service{serviceSegments.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="p-5">
            {usageQuery.isLoading ? (
              <div className="flex flex-col items-center gap-4">
                <div
                  className="h-36 w-36 animate-pulse rounded-full"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                />
                <div className="w-full space-y-2">
                  {[80, 60, 40].map((w) => (
                    <div
                      key={w}
                      className="h-4 animate-pulse rounded"
                      style={{ width: `${w}%`, background: 'rgba(255,255,255,0.05)' }}
                    />
                  ))}
                </div>
              </div>
            ) : serviceSegments.length > 0 ? (
              <DonutChart
                segments={serviceSegments}
                size={152}
                thickness={18}
                centerValue={serviceTotal}
                centerLabel="Total"
              />
            ) : (
              <SectionEmptyState title="No data" className="py-10" />
            )}
          </div>
        </div>
      </div>

      {/* Risk summary */}
      {risk && risk.totalEvents > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <RiskLevelCard label="Low risk"    count={risk.lowRiskCount}      color="#10b981" />
          <RiskLevelCard label="Medium risk" count={risk.mediumRiskCount}   color="#f59e0b" />
          <RiskLevelCard label="High risk"   count={risk.highRiskCount}     color="#f97316" />
          <RiskLevelCard label="Critical"    count={risk.criticalRiskCount} color="#ef4444" />
        </div>
      )}

      {/* Recommended actions */}
      <RecommendedActions
        kpis={kpis}
        risk={risk}
        hasNoTraffic={!usageQuery.isLoading && (usage?.series ?? []).every((s) => s.total === 0)}
        balanceValid={balanceValid}
        daysUntilExpiry={daysUntilExpiry}
      />

      {/* Quick actions */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-fg">Quick Actions</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickLink
            to="/portal/risk-events"
            icon={Shield}
            label="Risk Events"
            description="Browse all fraud & trust events"
            color="text-emerald-600 bg-emerald-50 dark:bg-emerald-500/15 dark:text-emerald-400"
          />
          <QuickLink
            to="/portal/usage"
            icon={TrendingUp}
            label="Usage Analytics"
            description="Detailed breakdown & trends"
            color="text-primary-600 bg-primary-50 dark:bg-primary-500/15 dark:text-primary-400"
          />
          <QuickLink
            to="/portal/docs"
            icon={BookOpen}
            label="Developer Docs"
            description="Integration guides & API reference"
            color="text-violet-600 bg-violet-50 dark:bg-violet-500/15 dark:text-violet-400"
          />
          <QuickLink
            to="/portal/team"
            icon={Users}
            label="Team & Access"
            description="Manage teammates and roles"
            color="text-cyan-600 bg-cyan-50 dark:bg-cyan-500/15 dark:text-cyan-400"
          />
        </div>
      </div>
    </div>
  );
}
